import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../components/auth/AuthContext";
import { HeaderBar } from "../components/layout/TopBar";
import NavBar from "../components/layout/NavBar";
import { TagArt } from "../components/adminSurface/heroArt";
import {
  getDatabases,
  getClassificationRules,
  createClassificationRule,
  updateClassificationRule,
  deleteClassificationRule,
  testClassificationRule,
  getClassificationStarters,
  cloneClassificationStarter,
  triggerClassificationBackfill,
  getClassificationBackfillStatus,
  cancelClassificationBackfill,
  getDocumentClassification,
  overrideDocumentClassification,
  getDocumentClassificationHistory,
} from "../api/adminApi";

const CLF_STYLES = `
.clf-hero{ position:relative; overflow:hidden; border-radius:18px; padding:24px 28px; margin-bottom:20px;
  background:linear-gradient(135deg,#312e81 0%,#4338ca 46%,#6d28d9 100%);
  box-shadow:0 14px 38px -10px rgba(67,56,202,.55); display:flex; align-items:center; gap:20px; }
.clf-hero__glow{ position:absolute; inset:0; pointer-events:none;
  background:radial-gradient(ellipse at 80% 25%, rgba(255,255,255,.16) 0%, transparent 55%); }
.clf-hero__logo{ position:relative; flex:0 0 auto; display:flex; align-items:center; pointer-events:none; }
.clf-hero__logo svg{ display:block; height:82px; width:auto; }
.clf-hero__body{ position:relative; flex:1; min-width:0; }
.clf-hero__title{ margin:0; font-size:23px; font-weight:800; color:#fff; letter-spacing:-.01em; }
.clf-hero__sub{ margin:5px 0 0; font-size:13px; color:rgba(255,255,255,.78); }
.clf-hero__stat{ position:relative; display:flex; flex-direction:column; align-items:flex-end; padding-right:4px; }
.clf-hero__stat b{ font-size:28px; font-weight:800; color:#fff; line-height:1; }
.clf-hero__stat span{ display:inline-flex; align-items:center; gap:6px; font-size:11px; text-transform:uppercase;
  letter-spacing:.08em; color:rgba(255,255,255,.72); margin-top:6px; }
.clf-refresh{ position:relative; appearance:none; width:40px; height:40px; border-radius:11px; flex:0 0 auto;
  border:1px solid rgba(255,255,255,.26); background:rgba(255,255,255,.12); color:#fff; font-size:17px; cursor:pointer;
  transition:background .15s, transform .12s; display:flex; align-items:center; justify-content:center; }
.clf-refresh:hover{ background:rgba(255,255,255,.24); }
.clf-refresh:active{ transform:translateY(1px); }
.clf-refresh:disabled{ opacity:.6; cursor:not-allowed; }

.clf-card{ background:#fff; border:1px solid #eef0f5; border-radius:16px; padding:20px 22px; margin-bottom:16px;
  box-shadow:0 10px 34px -14px rgba(15,23,42,.14); }
.clf-cardhd{ display:flex; align-items:center; gap:10px; margin:0 0 14px; }
.clf-cardhd h2{ margin:0; font-size:15px; font-weight:800; color:#1e293b; letter-spacing:-.01em; }
.clf-cardhd .clf-sub{ font-size:12px; color:#94a3b8; }
.clf-grid{ display:flex; gap:14px; flex-wrap:wrap; align-items:flex-end; }
.clf-field{ display:flex; flex-direction:column; min-width:0; }
.clf-lbl{ font-size:10.5px; font-weight:700; color:#64748b; text-transform:uppercase; letter-spacing:.07em; margin-bottom:6px; }
.clf-input{ width:100%; box-sizing:border-box; padding:10px 12px; border-radius:10px; border:1px solid #e2e8f0;
  font-size:13.5px; font-family:inherit; background:#fff; color:#0f172a; transition:border-color .15s, box-shadow .15s; }
.clf-input:hover{ border-color:#cbd5e1; }
.clf-input:focus{ outline:none; border-color:#7c3aed; box-shadow:0 0 0 3px rgba(124,58,237,.15); }
.clf-input::placeholder{ color:#94a3b8; }
textarea.clf-input{ resize:vertical; min-height:60px; line-height:1.5; font-family:ui-monospace, monospace; font-size:12.5px; }

.clf-seg{ display:inline-flex; background:#f1f5f9; border:1px solid #e6eaf1; border-radius:11px; padding:3px; gap:2px; }
.clf-seg button{ appearance:none; border:none; background:transparent; color:#475569; font-size:12.5px; font-weight:600;
  padding:8px 14px; border-radius:8px; cursor:pointer; transition:all .15s; white-space:nowrap; }
.clf-seg button:hover{ color:#1e293b; }
.clf-seg button.on{ background:#fff; color:#6d28d9; box-shadow:0 1px 3px rgba(15,23,42,.14); }

.clf-cta{ appearance:none; border:none; cursor:pointer; color:#fff; font-weight:700; font-size:14px; padding:11px 24px;
  border-radius:12px; background:linear-gradient(135deg,#4338ca,#6d28d9 55%,#7c3aed);
  box-shadow:0 8px 20px -4px rgba(109,40,217,.42); transition:filter .15s, box-shadow .15s, transform .12s;
  display:inline-flex; align-items:center; gap:8px; }
.clf-cta:hover{ filter:brightness(1.06); box-shadow:0 10px 26px -4px rgba(109,40,217,.5); }
.clf-cta:active{ transform:translateY(1px); }
.clf-cta:disabled{ opacity:.6; cursor:not-allowed; filter:none; box-shadow:none; }
.clf-cta.ghost{ background:#f1f5f9; color:#4338ca; box-shadow:none; border:1px solid #e2e8f0; }
.clf-cta.ghost:hover{ background:#eae7fb; }
.clf-cta.sm{ padding:7px 13px; font-size:12.5px; border-radius:9px; }
.clf-cta.danger{ background:linear-gradient(135deg,#b91c1c,#dc2626 60%,#ef4444); box-shadow:0 8px 20px -4px rgba(220,38,38,.42); }

.clf-tablecard{ background:#fff; border:1px solid #eef0f5; border-radius:16px; overflow:hidden;
  box-shadow:0 10px 34px -14px rgba(15,23,42,.12); margin-bottom:16px; }
.clf-table{ width:100%; border-collapse:collapse; }
.clf-table th{ text-align:left; font-size:10.5px; font-weight:700; color:#64748b; text-transform:uppercase;
  letter-spacing:.06em; padding:12px 16px; background:#f8fafc; border-bottom:1px solid #eef0f5; white-space:nowrap; }
.clf-table td{ padding:12px 16px; font-size:13px; color:#334155; border-bottom:1px solid #f1f5f9; vertical-align:middle; }
.clf-row td{ transition:background .12s; }
.clf-row:hover td{ background:#faf7ff; }

.clf-chip{ display:inline-block; padding:3px 9px; border-radius:7px; font-size:11px; font-weight:600;
  background:#f4f2fb; color:#5b21b6; border:1px solid #ece7fa; white-space:nowrap; }
.clf-chip.gray{ background:#f1f5f9; color:#64748b; border-color:#e2e8f0; }
.clf-tag{ display:inline-block; padding:2px 8px; border-radius:6px; font-size:10.5px; font-weight:700; letter-spacing:.02em; }
.clf-tag.pii{ background:#fee2e2; color:#b91c1c; } .clf-tag.pci{ background:#fef3c7; color:#92400e; }
.clf-tag.confidential{ background:#e0e7ff; color:#4338ca; } .clf-tag.internal{ background:#f1f5f9; color:#475569; }
.clf-tag.public{ background:#dcfce7; color:#15803d; }

.clf-empty{ padding:56px 40px; text-align:center; }
.clf-empty__ring{ width:76px; height:76px; margin:0 auto 16px; border-radius:22px; display:flex; align-items:center;
  justify-content:center; font-size:34px; background:linear-gradient(135deg,#eef2ff,#f5f0ff); border:1px solid #eae7fb; }
.clf-empty h3{ margin:0 0 6px; font-size:15px; color:#1e293b; } .clf-empty p{ margin:0; font-size:13px; color:#94a3b8; }

.clf-dd{ position:relative; }
.clf-dd__btn{ appearance:none; display:inline-flex; align-items:center; gap:9px; width:100%; box-sizing:border-box;
  padding:10px 12px; border-radius:10px; border:1px solid #e2e8f0; background:#fff; color:#0f172a; font-size:13.5px;
  font-family:inherit; cursor:pointer; transition:border-color .15s, box-shadow .15s; }
.clf-dd__btn:hover{ border-color:#cbd5e1; }
.clf-dd__btn.open{ border-color:#7c3aed; box-shadow:0 0 0 3px rgba(124,58,237,.15); }
.clf-dd__ico{ font-size:14px; opacity:.75; flex:0 0 auto; line-height:1; }
.clf-dd__lbl{ flex:1; text-align:left; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.clf-dd__lbl.ph{ color:#94a3b8; }
.clf-dd__chev{ flex:0 0 auto; font-size:11px; color:#94a3b8; transition:transform .15s, color .15s; }
.clf-dd__btn.open .clf-dd__chev{ transform:rotate(180deg); color:#7c3aed; }
.clf-dd__menu{ position:absolute; top:calc(100% + 6px); left:0; min-width:100%; z-index:300; box-sizing:border-box;
  background:#fff; border:1px solid #eef0f5; border-radius:12px; box-shadow:0 18px 44px -12px rgba(15,23,42,.3);
  padding:6px; max-height:288px; overflow-y:auto; }
.clf-dd__opt{ display:flex; align-items:center; gap:10px; padding:9px 11px; border-radius:9px; font-size:13.5px;
  color:#334155; cursor:pointer; white-space:nowrap; transition:background .1s; }
.clf-dd__opt:hover{ background:#f6f2ff; color:#1e293b; }
.clf-dd__opt.sel{ color:#6d28d9; font-weight:600; background:#f6f2ff; }
.clf-dd__dot{ width:8px; height:8px; border-radius:3px; background:#cbd5e1; flex:0 0 auto; }
.clf-dd__opt.sel .clf-dd__dot{ background:#7c3aed; }
.clf-dd__check{ margin-left:auto; color:#7c3aed; font-size:13px; opacity:0; }
.clf-dd__opt.sel .clf-dd__check{ opacity:1; }

.clf-switch{ position:relative; display:inline-flex; align-items:center; gap:10px; cursor:pointer; user-select:none;
  font-size:13px; font-weight:500; color:#334155; }
.clf-switch input{ position:absolute; opacity:0; width:0; height:0; }
.clf-switch__track{ width:42px; height:24px; border-radius:999px; background:#cbd5e1; position:relative;
  transition:background .18s, box-shadow .15s; flex:0 0 auto; }
.clf-switch__track::after{ content:""; position:absolute; top:2px; left:2px; width:20px; height:20px; border-radius:50%;
  background:#fff; box-shadow:0 1px 3px rgba(15,23,42,.32); transition:transform .18s; }
.clf-switch input:checked + .clf-switch__track{ background:linear-gradient(135deg,#6d28d9,#7c3aed); }
.clf-switch input:checked + .clf-switch__track::after{ transform:translateX(18px); }

.clf-result{ margin-top:14px; padding:12px 16px; border-radius:12px; font-size:13px; display:flex; align-items:center; gap:10px; }
.clf-result.ok{ background:#f5f3ff; border:1px solid #e9e5fb; color:#5b21b6; }
.clf-result.none{ background:#f8fafc; border:1px solid #e2e8f0; color:#64748b; }
.clf-bar{ height:8px; border-radius:999px; background:#eef2ff; overflow:hidden; margin-top:8px; }
.clf-bar > i{ display:block; height:100%; background:linear-gradient(90deg,#6d28d9,#7c3aed); transition:width .3s; }
.clf-note{ font-size:12px; color:#94a3b8; margin:8px 0 0; }
.clf-err{ margin-bottom:14px; padding:11px 15px; border-radius:12px; background:#fee2e2; border:1px solid #fecaca; color:#b91c1c; font-size:13px; }
`;

function Dropdown({ value, onChange, options, placeholder, icon, disabled }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const sel = options.find((o) => String(o.value) === String(value));
  return (
    <div className="clf-dd" ref={ref}>
      <button type="button" className={`clf-dd__btn${open ? " open" : ""}`} disabled={disabled}
        onClick={() => setOpen((o) => !o)}>
        {icon && <span className="clf-dd__ico">{icon}</span>}
        <span className={`clf-dd__lbl${sel ? "" : " ph"}`}>{sel ? sel.label : placeholder}</span>
        <span className="clf-dd__chev">▾</span>
      </button>
      {open && (
        <div className="clf-dd__menu">
          {options.map((o) => {
            const on = String(o.value) === String(value);
            return (
              <div key={o.value} className={`clf-dd__opt${on ? " sel" : ""}`}
                onClick={() => { onChange(o.value); setOpen(false); }}>
                <span className="clf-dd__dot" />
                <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{o.label}</span>
                <span className="clf-dd__check">✓</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const dbId = (d) => d.id ?? d.Id ?? d.databaseId ?? d.DatabaseId;
const dbName = (d) => d.dbName ?? d.DbName ?? d.name ?? d.Name ?? String(dbId(d));
const MATCH_TYPES = ["Keyword", "Regex", "Field", "Luhn"];
const SENSITIVITIES = ["PII", "PCI", "Confidential", "Internal", "Public"];

const emptyForm = () => ({
  id: null, name: "", description: "", matchType: "Keyword", pattern: "", targetField: "",
  label: "", sensitivity: "", confidence: 100, priority: 0, scopeDbId: "", enabled: true,
});

export default function ClassificationPage() {
  const { t } = useTranslation();
  const { token } = useAuth();

  const [databases, setDatabases] = useState([]);
  const [rules, setRules] = useState([]);
  const [starters, setStarters] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);

  // test panel
  const [testFields, setTestFields] = useState("DocType=Payslip\nAccount=TR33 0006 1005 1978 6457 8413 26");
  const [testFileName, setTestFileName] = useState("");
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  // backfill
  const [backfillDbId, setBackfillDbId] = useState("");
  const [onlyUnclassified, setOnlyUnclassified] = useState(true);
  const [backfill, setBackfill] = useState(null);

  // per-document override
  const [ovrDbId, setOvrDbId] = useState("");
  const [ovrArchiveNo, setOvrArchiveNo] = useState("");
  const [ovrDoc, setOvrDoc] = useState(null); // { label } once looked up
  const [ovrNewLabel, setOvrNewLabel] = useState("");
  const [ovrNote, setOvrNote] = useState("");
  const [ovrHistory, setOvrHistory] = useState([]);
  const [ovrBusy, setOvrBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadRules = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError("");
    try {
      const data = await getClassificationRules(token);
      setRules(Array.isArray(data) ? data : (data?.items ?? []));
    } catch (e) {
      setError(e?.message || t("classification.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [token, t]);

  useEffect(() => {
    if (!token) return;
    getDatabases(token)
      .then((d) => setDatabases(Array.isArray(d) ? d : (d?.databases ?? d?.Databases ?? [])))
      .catch(() => {});
    getClassificationStarters(token).then((s) => setStarters(Array.isArray(s) ? s : [])).catch(() => {});
  }, [token]);

  useEffect(() => { loadRules(); }, [loadRules]);

  const activeCount = useMemo(() => rules.filter((r) => r.enabled ?? r.Enabled).length, [rules]);

  // Field names available to target — from the scoped database, or the union across all databases when
  // the rule is org-wide. Falls back gracefully if a database exposes no index metadata.
  const collectFields = (db) =>
    (db?.relatedIndexes ?? db?.RelatedIndexes ?? db?.searchableNames ?? db?.SearchableNames ?? [])
      .map((f) => (typeof f === "string" ? f : (f?.fieldName ?? f?.FieldName)))
      .filter(Boolean);

  const targetFieldOptions = useMemo(() => {
    let names = [];
    if (form.scopeDbId) {
      const db = databases.find((d) => String(dbId(d)) === String(form.scopeDbId));
      names = db ? collectFields(db) : [];
    } else {
      const set = new Set();
      databases.forEach((d) => collectFields(d).forEach((n) => set.add(n)));
      names = [...set];
    }
    const opts = [
      { value: "", label: t("classification.targetFieldPlaceholder") },
      { value: "FileName", label: t("classification.testFileName") },
    ];
    const seen = new Set(["", "filename"]);
    names.forEach((n) => {
      if (n && !seen.has(n.toLowerCase())) { opts.push({ value: n, label: n }); seen.add(n.toLowerCase()); }
    });
    // Keep an already-set value visible even if it isn't in the current scope's field list.
    if (form.targetField && !seen.has(form.targetField.toLowerCase())) {
      opts.push({ value: form.targetField, label: form.targetField });
    }
    return opts;
  }, [databases, form.scopeDbId, form.targetField, t]);

  const buildBody = () => ({
    name: form.name.trim(),
    description: form.description || null,
    enabled: form.enabled,
    priority: Number(form.priority) || 0,
    sourceDBInfoId: form.scopeDbId ? Number(form.scopeDbId) : null,
    matchType: form.matchType,
    pattern: form.pattern || null,
    targetField: form.targetField || null,
    label: form.label.trim(),
    confidence: Number(form.confidence),
    sensitivity: form.sensitivity || null,
  });

  const save = async () => {
    setSaving(true); setError("");
    try {
      if (form.id) await updateClassificationRule(token, form.id, buildBody());
      else await createClassificationRule(token, buildBody());
      setForm(emptyForm());
      await loadRules();
    } catch (e) {
      setError(e?.message || t("classification.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const edit = (r) => setForm({
    id: r.id ?? r.Id, name: r.name ?? r.Name ?? "", description: r.description ?? r.Description ?? "",
    matchType: r.matchType ?? r.MatchType ?? "Keyword", pattern: r.pattern ?? r.Pattern ?? "",
    targetField: r.targetField ?? r.TargetField ?? "", label: r.label ?? r.Label ?? "",
    sensitivity: r.sensitivity ?? r.Sensitivity ?? "", confidence: r.confidence ?? r.Confidence ?? 100,
    priority: r.priority ?? r.Priority ?? 0, scopeDbId: (r.sourceDBInfoId ?? r.SourceDBInfoId) ? String(r.sourceDBInfoId ?? r.SourceDBInfoId) : "",
    enabled: r.enabled ?? r.Enabled ?? true,
  });

  const toggle = async (r) => {
    try {
      await updateClassificationRule(token, r.id ?? r.Id, {
        name: r.name ?? r.Name, matchType: r.matchType ?? r.MatchType, pattern: r.pattern ?? r.Pattern,
        targetField: r.targetField ?? r.TargetField, label: r.label ?? r.Label,
        sensitivity: r.sensitivity ?? r.Sensitivity, confidence: r.confidence ?? r.Confidence,
        priority: r.priority ?? r.Priority, sourceDBInfoId: r.sourceDBInfoId ?? r.SourceDBInfoId,
        organization: r.organization ?? r.Organization, enabled: !(r.enabled ?? r.Enabled),
      });
      await loadRules();
    } catch (e) { setError(e?.message || t("classification.saveFailed")); }
  };

  const remove = async (r) => {
    if (!window.confirm(t("classification.confirmDelete"))) return;
    try { await deleteClassificationRule(token, r.id ?? r.Id); await loadRules(); }
    catch (e) { setError(e?.message || t("classification.deleteFailed")); }
  };

  const cloneStarter = async (s) => {
    try { await cloneClassificationStarter(token, s.id ?? s.Id, {}); await loadRules(); }
    catch (e) { setError(e?.message || t("classification.saveFailed")); }
  };

  const runTest = async () => {
    setTesting(true); setTestResult(null); setError("");
    try {
      const fields = {};
      testFields.split("\n").forEach((line) => {
        const i = line.indexOf("=");
        if (i > 0) fields[line.slice(0, i).trim()] = line.slice(i + 1).trim();
      });
      const res = await testClassificationRule(token, { fields, fileName: testFileName || null });
      setTestResult(res);
    } catch (e) {
      setError(e?.message || t("classification.testFailed"));
    } finally {
      setTesting(false);
    }
  };

  const refreshBackfill = useCallback(async (id) => {
    if (!token || !id) return;
    try { setBackfill(await getClassificationBackfillStatus(token, id)); } catch { /* ignore */ }
  }, [token]);

  useEffect(() => {
    if (!backfillDbId) { setBackfill(null); return; }
    refreshBackfill(backfillDbId);
  }, [backfillDbId, refreshBackfill]);

  // poll while a backfill is running
  useEffect(() => {
    if (!backfill || backfill.state !== "Running") return;
    const h = setInterval(() => refreshBackfill(backfillDbId), 2000);
    return () => clearInterval(h);
  }, [backfill, backfillDbId, refreshBackfill]);

  const startBackfill = async () => {
    if (!backfillDbId) return;
    setError("");
    try {
      const job = await triggerClassificationBackfill(token, { databaseId: Number(backfillDbId), onlyUnclassified });
      setBackfill(job);
    } catch (e) { setError(e?.message || t("classification.backfillFailed")); }
  };

  const cancelBackfill = async () => {
    try { await cancelClassificationBackfill(token, Number(backfillDbId)); refreshBackfill(backfillDbId); }
    catch { /* ignore */ }
  };

  const loadOvrHistory = useCallback(async (dbId_, archiveNo) => {
    try { setOvrHistory(await getDocumentClassificationHistory(token, dbId_, archiveNo) || []); }
    catch { setOvrHistory([]); }
  }, [token]);

  const lookupDoc = async () => {
    if (!ovrDbId || !ovrArchiveNo.trim()) return;
    setOvrBusy(true); setOvrDoc(null); setOvrHistory([]); setError("");
    try {
      const doc = await getDocumentClassification(token, Number(ovrDbId), ovrArchiveNo.trim());
      setOvrDoc(doc);
      setOvrNewLabel(doc?.label ?? "");
      await loadOvrHistory(Number(ovrDbId), ovrArchiveNo.trim());
    } catch (e) {
      setError(e?.message || t("classification.docNotFound"));
    } finally {
      setOvrBusy(false);
    }
  };

  const saveOverride = async () => {
    if (!ovrDbId || !ovrArchiveNo.trim()) return;
    setOvrBusy(true); setError("");
    try {
      const res = await overrideDocumentClassification(token, {
        databaseId: Number(ovrDbId), archiveNo: ovrArchiveNo.trim(),
        label: ovrNewLabel.trim() || null, note: ovrNote.trim() || null,
      });
      setOvrDoc({ ...ovrDoc, label: res?.label ?? null });
      setOvrNote("");
      await loadOvrHistory(Number(ovrDbId), ovrArchiveNo.trim());
    } catch (e) {
      setError(e?.message || t("classification.saveFailed"));
    } finally {
      setOvrBusy(false);
    }
  };

  const scopeLabel = (r) => {
    const s = r.sourceDBInfoId ?? r.SourceDBInfoId;
    if (!s) return t("classification.orgWide");
    const d = databases.find((x) => String(dbId(x)) === String(s));
    return d ? dbName(d) : `#${s}`;
  };

  return (
    <div className="app-root">
      <div className="nav-strip"><NavBar /></div>
      <HeaderBar />
      <style>{CLF_STYLES}</style>

      <div className="page-container" style={{ paddingBottom: 40 }}>
        <div className="clf-hero">
          <div className="clf-hero__glow" />
          <div className="clf-hero__logo"><TagArt /></div>
          <div className="clf-hero__body">
            <h1 className="clf-hero__title">{t("classification.title")}</h1>
            <p className="clf-hero__sub">{t("classification.subtitle")}</p>
          </div>
          <div className="clf-hero__stat">
            <b>{activeCount}</b>
            <span>{t("classification.activeRules")}</span>
          </div>
          <button className="clf-refresh" onClick={loadRules} disabled={loading} title={t("classification.refresh")}>
            {loading ? "⟳" : "↺"}
          </button>
        </div>

        {error && <div className="clf-err">{error}</div>}

        {/* ── Composer ── */}
        <div className="clf-card">
          <div className="clf-cardhd">
            <h2>{form.id ? t("classification.editRule") : t("classification.newRule")}</h2>
            <span className="clf-sub">{t("classification.composerHint")}</span>
          </div>
          <div className="clf-grid">
            <div className="clf-field" style={{ flex: "2 1 220px" }}>
              <label className="clf-lbl">{t("classification.name")}</label>
              <input className="clf-input" value={form.name} onChange={(e) => set("name", e.target.value)}
                placeholder={t("classification.namePlaceholder")} />
            </div>
            <div className="clf-field" style={{ flex: "1 1 160px" }}>
              <label className="clf-lbl">{t("classification.label")}</label>
              <input className="clf-input" value={form.label} onChange={(e) => set("label", e.target.value)}
                placeholder="PII" />
            </div>
            <div className="clf-field" style={{ flex: "1 1 150px" }}>
              <label className="clf-lbl">{t("classification.sensitivity")}</label>
              <Dropdown icon="🔖" value={form.sensitivity} onChange={(v) => set("sensitivity", v)}
                placeholder="—" options={[{ value: "", label: "—" }, ...SENSITIVITIES.map((s) => ({ value: s, label: s }))]} />
            </div>
          </div>

          <div className="clf-grid" style={{ marginTop: 14 }}>
            <div className="clf-field" style={{ flex: "0 0 auto" }}>
              <label className="clf-lbl">{t("classification.matchType")}</label>
              <div className="clf-seg">
                {MATCH_TYPES.map((m) => (
                  <button key={m} className={form.matchType === m ? "on" : ""} onClick={() => set("matchType", m)}>
                    {t(`classification.match_${m.toLowerCase()}`)}
                  </button>
                ))}
              </div>
            </div>
            <div className="clf-field" style={{ flex: "1 1 140px" }}>
              <label className="clf-lbl">{t("classification.targetField")}</label>
              <Dropdown icon="🔎" value={form.targetField} onChange={(v) => set("targetField", v)}
                placeholder={t("classification.targetFieldPlaceholder")} options={targetFieldOptions} />
            </div>
            <div className="clf-field" style={{ flex: "1 1 130px" }}>
              <label className="clf-lbl">{t("classification.scope")}</label>
              <Dropdown icon="🗄" value={form.scopeDbId} onChange={(v) => set("scopeDbId", v)}
                placeholder={t("classification.orgWide")}
                options={[{ value: "", label: t("classification.orgWide") }, ...databases.map((d) => ({ value: String(dbId(d)), label: dbName(d) }))]} />
            </div>
          </div>

          {form.matchType !== "Luhn" && (
            <div className="clf-field" style={{ marginTop: 14 }}>
              <label className="clf-lbl">{t(`classification.pattern_${form.matchType.toLowerCase()}`)}</label>
              <textarea className="clf-input" rows={2} value={form.pattern} onChange={(e) => set("pattern", e.target.value)}
                placeholder={t(`classification.patternPlaceholder_${form.matchType.toLowerCase()}`)} />
            </div>
          )}

          <div className="clf-grid" style={{ marginTop: 14, alignItems: "center" }}>
            <div className="clf-field" style={{ flex: "0 0 110px" }}>
              <label className="clf-lbl">{t("classification.confidence")}</label>
              <input className="clf-input" type="number" min={0} max={100} value={form.confidence}
                onChange={(e) => set("confidence", e.target.value)} />
            </div>
            <div className="clf-field" style={{ flex: "0 0 110px" }}>
              <label className="clf-lbl">{t("classification.priority")}</label>
              <input className="clf-input" type="number" value={form.priority} onChange={(e) => set("priority", e.target.value)} />
            </div>
            <label className="clf-switch" style={{ marginTop: 18 }}>
              <input type="checkbox" checked={form.enabled} onChange={(e) => set("enabled", e.target.checked)} />
              <span className="clf-switch__track" />
              {t("classification.enabled")}
            </label>
            <div style={{ marginLeft: "auto", display: "flex", gap: 10, marginTop: 12 }}>
              {form.id && <button className="clf-cta ghost" onClick={() => setForm(emptyForm())}>{t("common.cancel")}</button>}
              <button className="clf-cta" onClick={save} disabled={saving || !form.name.trim() || !form.label.trim()}>
                {saving ? t("classification.saving") : (form.id ? t("classification.updateRule") : t("classification.addRule"))}
              </button>
            </div>
          </div>
        </div>

        {/* ── Rules table ── */}
        <div className="clf-tablecard">
          {rules.length === 0 ? (
            <div className="clf-empty">
              <div className="clf-empty__ring">🏷️</div>
              <h3>{t("classification.emptyTitle")}</h3>
              <p>{t("classification.emptyHint")}</p>
            </div>
          ) : (
            <table className="clf-table">
              <thead>
                <tr>
                  <th>{t("classification.priority")}</th>
                  <th>{t("classification.name")}</th>
                  <th>{t("classification.matchType")}</th>
                  <th>{t("classification.label")}</th>
                  <th>{t("classification.scope")}</th>
                  <th>{t("classification.confidence")}</th>
                  <th>{t("classification.enabled")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rules.map((r) => {
                  const sens = (r.sensitivity ?? r.Sensitivity ?? "").toLowerCase();
                  return (
                    <tr key={r.id ?? r.Id} className="clf-row">
                      <td><span className="clf-chip gray">{r.priority ?? r.Priority}</span></td>
                      <td style={{ fontWeight: 600, color: "#1e293b" }}>{r.name ?? r.Name}</td>
                      <td><span className="clf-chip">{t(`classification.match_${String(r.matchType ?? r.MatchType).toLowerCase()}`)}</span></td>
                      <td>
                        {r.label ?? r.Label}
                        {sens && <span className={`clf-tag ${sens}`} style={{ marginLeft: 6 }}>{r.sensitivity ?? r.Sensitivity}</span>}
                      </td>
                      <td style={{ color: "#64748b" }}>{scopeLabel(r)}</td>
                      <td>{r.confidence ?? r.Confidence}%</td>
                      <td>
                        <label className="clf-switch">
                          <input type="checkbox" checked={!!(r.enabled ?? r.Enabled)} onChange={() => toggle(r)} />
                          <span className="clf-switch__track" />
                        </label>
                      </td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        <button className="clf-cta ghost sm" onClick={() => edit(r)}>{t("classification.edit")}</button>{" "}
                        <button className="clf-cta danger sm" onClick={() => remove(r)}>{t("classification.delete")}</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Test panel ── */}
        <div className="clf-card">
          <div className="clf-cardhd">
            <h2>{t("classification.testTitle")}</h2>
            <span className="clf-sub">{t("classification.testHint")}</span>
          </div>
          <div className="clf-grid">
            <div className="clf-field" style={{ flex: "2 1 280px" }}>
              <label className="clf-lbl">{t("classification.testFields")}</label>
              <textarea className="clf-input" rows={4} value={testFields} onChange={(e) => setTestFields(e.target.value)}
                placeholder={"DocType=Invoice\nAccount=TR33..."} />
            </div>
            <div className="clf-field" style={{ flex: "1 1 180px" }}>
              <label className="clf-lbl">{t("classification.testFileName")}</label>
              <input className="clf-input" value={testFileName} onChange={(e) => setTestFileName(e.target.value)}
                placeholder="statement_2026.pdf" />
              <button className="clf-cta" style={{ marginTop: 12 }} onClick={runTest} disabled={testing}>
                {testing ? t("classification.testing") : t("classification.runTest")}
              </button>
            </div>
          </div>
          {testResult && (
            <div className={`clf-result ${testResult.matched ? "ok" : "none"}`}>
              {testResult.matched ? (
                <>
                  <span className={`clf-tag ${(testResult.sensitivity || "").toLowerCase()}`}>{testResult.label}</span>
                  <span>{t("classification.matchedBy", { rule: testResult.ruleName, confidence: testResult.confidence })}</span>
                </>
              ) : (
                <span>{t("classification.noMatch")}</span>
              )}
            </div>
          )}
        </div>

        {/* ── Starter library ── */}
        {starters.length > 0 && (
          <div className="clf-card">
            <div className="clf-cardhd">
              <h2>{t("classification.starterTitle")}</h2>
              <span className="clf-sub">{t("classification.starterHint")}</span>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              {starters.map((s) => (
                <div key={s.id ?? s.Id} className="clf-card" style={{ margin: 0, flex: "1 1 240px", minWidth: 220 }}>
                  <div style={{ fontWeight: 700, color: "#1e293b", marginBottom: 4 }}>{s.name ?? s.Name}</div>
                  <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 10, minHeight: 32 }}>{s.description ?? s.Description}</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span className={`clf-tag ${(s.sensitivity ?? s.Sensitivity ?? "").toLowerCase()}`}>{s.label ?? s.Label}</span>
                    <span className="clf-chip">{t(`classification.match_${String(s.matchType ?? s.MatchType).toLowerCase()}`)}</span>
                    <button className="clf-cta ghost sm" style={{ marginLeft: "auto" }} onClick={() => cloneStarter(s)}>
                      {t("classification.clone")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Per-document override ── */}
        <div className="clf-card">
          <div className="clf-cardhd">
            <h2>{t("classification.overrideTitle")}</h2>
            <span className="clf-sub">{t("classification.overrideHint")}</span>
          </div>
          <div className="clf-grid">
            <div className="clf-field" style={{ flex: "1 1 200px" }}>
              <label className="clf-lbl">{t("classification.database")}</label>
              <Dropdown icon="🗄" value={ovrDbId} onChange={(v) => { setOvrDbId(v); setOvrDoc(null); setOvrHistory([]); }}
                placeholder={t("classification.selectDatabase")}
                options={databases.map((d) => ({ value: String(dbId(d)), label: dbName(d) }))} />
            </div>
            <div className="clf-field" style={{ flex: "2 1 220px" }}>
              <label className="clf-lbl">{t("classification.archiveNo")}</label>
              <input className="clf-input" value={ovrArchiveNo} onChange={(e) => setOvrArchiveNo(e.target.value)}
                placeholder={t("classification.archiveNoPlaceholder")} onKeyDown={(e) => e.key === "Enter" && lookupDoc()} />
            </div>
            <button className="clf-cta ghost" style={{ marginTop: 18 }} onClick={lookupDoc} disabled={ovrBusy || !ovrDbId || !ovrArchiveNo.trim()}>
              {t("classification.lookUp")}
            </button>
          </div>

          {ovrDoc && (
            <>
              <div className="clf-grid" style={{ marginTop: 14, alignItems: "flex-end" }}>
                <div className="clf-field" style={{ flex: "0 0 auto" }}>
                  <label className="clf-lbl">{t("classification.currentLabel")}</label>
                  <div>{ovrDoc.label ? <span className={`clf-tag ${(ovrDoc.label || "").toLowerCase()}`}>{ovrDoc.label}</span> : <span className="clf-chip gray">{t("classification.unclassified")}</span>}</div>
                </div>
                <div className="clf-field" style={{ flex: "1 1 160px" }}>
                  <label className="clf-lbl">{t("classification.newLabel")}</label>
                  <input className="clf-input" value={ovrNewLabel} onChange={(e) => setOvrNewLabel(e.target.value)}
                    placeholder={t("classification.newLabelPlaceholder")} />
                </div>
                <div className="clf-field" style={{ flex: "2 1 220px" }}>
                  <label className="clf-lbl">{t("classification.overrideNote")}</label>
                  <input className="clf-input" value={ovrNote} onChange={(e) => setOvrNote(e.target.value)}
                    placeholder={t("classification.overrideNotePlaceholder")} />
                </div>
                <button className="clf-cta" style={{ marginTop: 12 }} onClick={saveOverride} disabled={ovrBusy}>
                  {t("classification.saveOverride")}
                </button>
              </div>

              {ovrHistory.length > 0 && (
                <div className="clf-tablecard" style={{ marginTop: 14 }}>
                  <table className="clf-table">
                    <thead>
                      <tr>
                        <th>{t("classification.when")}</th>
                        <th>{t("classification.change")}</th>
                        <th>{t("classification.source")}</th>
                        <th>{t("classification.by")}</th>
                        <th>{t("classification.overrideNote")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ovrHistory.map((h) => (
                        <tr key={h.id} className="clf-row">
                          <td style={{ color: "#64748b", whiteSpace: "nowrap" }}>{new Date(h.classifiedAt).toLocaleString()}</td>
                          <td>
                            {h.prevLabel ? <span className="clf-chip gray">{h.prevLabel}</span> : <span className="clf-chip gray">—</span>}
                            {" → "}
                            {h.label ? <span className={`clf-tag ${(h.label || "").toLowerCase()}`}>{h.label}</span> : <span className="clf-chip gray">—</span>}
                          </td>
                          <td><span className="clf-chip">{h.source}</span></td>
                          <td style={{ color: "#64748b" }}>{h.classifiedBy || "—"}</td>
                          <td style={{ color: "#64748b" }}>{h.detail || ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        {/* ── Backfill ── */}
        <div className="clf-card">
          <div className="clf-cardhd">
            <h2>{t("classification.backfillTitle")}</h2>
            <span className="clf-sub">{t("classification.backfillHint")}</span>
          </div>
          <div className="clf-grid">
            <div className="clf-field" style={{ flex: "1 1 200px" }}>
              <label className="clf-lbl">{t("classification.database")}</label>
              <Dropdown icon="🗄" value={backfillDbId} onChange={setBackfillDbId} placeholder={t("classification.selectDatabase")}
                options={databases.map((d) => ({ value: String(dbId(d)), label: dbName(d) }))} />
            </div>
            <label className="clf-switch" style={{ marginTop: 18 }}>
              <input type="checkbox" checked={onlyUnclassified} onChange={(e) => setOnlyUnclassified(e.target.checked)} />
              <span className="clf-switch__track" />
              {t("classification.onlyUnclassified")}
            </label>
            <div style={{ marginLeft: "auto", display: "flex", gap: 10, marginTop: 12 }}>
              {backfill?.state === "Running" && (
                <button className="clf-cta ghost" onClick={cancelBackfill}>{t("classification.cancel")}</button>
              )}
              <button className="clf-cta" onClick={startBackfill} disabled={!backfillDbId || backfill?.state === "Running"}>
                {t("classification.startBackfill")}
              </button>
            </div>
          </div>
          {backfill && (
            <div style={{ marginTop: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "#475569" }}>
                <span>
                  <span className={`clf-chip ${backfill.state === "Running" ? "" : "gray"}`}>{t(`classification.state_${String(backfill.state).toLowerCase()}`)}</span>
                  {"  "}{t("classification.scanned")}: <b>{backfill.scanned ?? 0}</b> · {t("classification.labeled")}: <b>{backfill.labeled ?? 0}</b>
                </span>
                {backfill.error && <span style={{ color: "#b91c1c" }}>{backfill.error}</span>}
              </div>
              <div className="clf-bar"><i style={{ width: backfill.state === "Running" ? "66%" : "100%" }} /></div>
              <p className="clf-note">{t("classification.backfillNote")}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
